import * as React from 'react';
import { clsx } from 'clsx';

export interface ModalFooterProps {
  /**
   * The content of the footer, typically buttons
   */
  children: React.ReactNode;
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * Additional inline styles
   */
  style?: React.CSSProperties;
}

// Arbor Design System modal footer styles
const modalFooterStyles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    width: '100%',
    padding: '16px',
    backgroundColor: '#ffffff',
    boxSizing: 'border-box' as const,
    maxHeight: '56px',
  },
};

/**
 * ModalFooter component - Arbor Design System
 *
 * The footer part of a modal dialog, typically containing action buttons.
 * Buttons are right-aligned by default.
 *
 * @example
 * ```tsx
 * // Footer with two buttons
 * <ModalFooter>
 *   <Button variant="tertiary">Cancel</Button>
 *   <Button variant="primary">Save</Button>
 * </ModalFooter>
 *
 * // Footer with destructive action
 * <ModalFooter>
 *   <Button variant="tertiary">Cancel</Button>
 *   <Button variant="destructive-secondary">Delete</Button>
 * </ModalFooter>
 * ```
 */
export const ModalFooter = React.forwardRef<HTMLDivElement, ModalFooterProps>(
  ({ children, className, style, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx('arbor-modal-footer', className)}
        style={{ ...modalFooterStyles.container, ...style }}
        {...props}
      >
        {children}
      </div>
    );
  }
);

ModalFooter.displayName = 'ModalFooter';
